/**
 * The **chart** widget — 9 of the 10 widgets this account uses (`specs/M10-D2-widgets.md` §5.1).
 *
 * Its settings are not a design: they are monday's own `settings_str` vocabulary, taken key for
 * key from the 11 readable `GraphBoardView` rows (§2.2), because a chart view and a chart widget
 * are the same feature family and the view's settings *are* readable while the widget's are not.
 * `toMondaySettings` therefore re-serialises byte-identically — misspelling
 * (`show_comulative_data`) included, and unknown keys carried through untouched.
 */
import { z } from 'zod';
import { defineWidget, widgetPixelHeight } from '../types.js';
/** The y-axis entry that means *count of items* rather than a column `[gbv]`. */
export const COUNT_MEASURE_KEY = 'default-label-count';
/** The x-axis entry that means *the item's group* rather than a column `[gbv]`. */
export const GROUP_DIMENSION_KEY = 'group';
/** monday's three observed values, plus the four its picker offers. */
export const GRAPH_TYPES = ['column', 'bar', 'line', 'pie', 'donut', 'stacked_column', 'area'];
export const DATE_BUCKETS = ['day', 'week', 'month', 'quarter', 'year'];
const axisMap = z.record(z.string(), z.boolean());
export const chartWidgetSettingsSchema = z.object({
    xAxisColumns: axisMap.default({}),
    yAxisColumns: axisMap.default({}),
    /** The stacking dimension. `{}` in all 11 rows; preserved so an import round-trips. */
    zAxisColumns: axisMap.default({}),
    guidelineBase: z.record(z.string(), z.unknown()).default({}),
    graphType: z.string().default('column'),
    emptyValues: z.boolean().default(false),
    groupBy: z.enum(DATE_BUCKETS).default('month'),
    flatLegendType: z.string().nullable().default(null),
    showCumulativeData: z.boolean().default(false),
    benchmarkLine: z
        .object({ label: z.string(), color: z.string(), value: z.string() })
        .nullable()
        .default(null),
    /** `null` = the key was absent, which is monday's own default (`sum`). */
    calcFunctionType: z.string().nullable().default(null),
    boardIds: z.array(z.string()).default([]),
    createdBy: z.number().nullable().default(null),
    /** Any `settings_str` key we do not model, kept so the round trip is lossless. */
    extra: z.record(z.string(), z.unknown()).default({}),
});
const KNOWN_KEYS = new Set([
    'x_axis_columns', 'y_axis_columns', 'z_axis_columns', 'guideline_base', 'graph_type',
    'empty_values', 'group_by', 'flat_legend_type', 'show_comulative_data',
    'benchmark_line_label', 'benchmark_line_color', 'benchmark_line_value',
    'calc_function_type', 'createdBy', 'board_ids',
]);
function asAxisMap(raw) {
    if (raw === null || typeof raw !== 'object')
        return {};
    const out = {};
    for (const [key, value] of Object.entries(raw))
        out[key] = value === true;
    return out;
}
/** The active entries of an axis map, in insertion order — monday's own series order. */
export function activeAxisKeys(map) {
    return Object.entries(map).filter(([, on]) => on).map(([key]) => key);
}
export const chartWidget = defineWidget({
    id: 'chart',
    kind: 'CHART',
    title: 'Chart',
    description: 'Count or sum your items by any column, as a column, bar, line or pie chart.',
    settingsSchema: chartWidgetSettingsSchema,
    defaultSettings: chartWidgetSettingsSchema.parse({}),
    dataShape: 'series',
    // The card measured on every production widget view: 505 x 416 px = 6 x 6 grid units.
    defaultLayout: { x: 0, y: 0, w: 6, h: 6 },
    minLayout: { w: 3, h: 4 },
    requiresBoards: true,
    implemented: true,
    plannedIn: null,
    galleryOrder: 1,
    fromMondaySettings(raw) {
        const source = typeof raw === 'string' ? safeParse(raw) : (raw ?? {});
        const benchmarkValue = source['benchmark_line_value'];
        const hasBenchmark = 'benchmark_line_value' in source || 'benchmark_line_color' in source || 'benchmark_line_label' in source;
        const extra = {};
        for (const [key, value] of Object.entries(source))
            if (!KNOWN_KEYS.has(key))
                extra[key] = value;
        const groupBy = String(source['group_by'] ?? 'month');
        return chartWidgetSettingsSchema.parse({
            xAxisColumns: asAxisMap(source['x_axis_columns']),
            yAxisColumns: asAxisMap(source['y_axis_columns']),
            zAxisColumns: asAxisMap(source['z_axis_columns']),
            guidelineBase: (source['guideline_base'] ?? {}),
            graphType: typeof source['graph_type'] === 'string' ? source['graph_type'] : 'column',
            emptyValues: source['empty_values'] === true,
            groupBy: DATE_BUCKETS.includes(groupBy) ? groupBy : 'month',
            flatLegendType: typeof source['flat_legend_type'] === 'string' ? source['flat_legend_type'] : null,
            showCumulativeData: source['show_comulative_data'] === true,
            benchmarkLine: hasBenchmark
                ? {
                    label: String(source['benchmark_line_label'] ?? ''),
                    color: String(source['benchmark_line_color'] ?? ''),
                    value: String(benchmarkValue ?? ''),
                }
                : null,
            calcFunctionType: typeof source['calc_function_type'] === 'string' ? source['calc_function_type'] : null,
            boardIds: Array.isArray(source['board_ids']) ? source['board_ids'].map(String) : [],
            createdBy: typeof source['createdBy'] === 'number' ? source['createdBy'] : null,
            extra,
        });
    },
    /**
     * Key order matters: `JSON.stringify` of the result must equal monday's own string for every
     * row in the corpus (`specs/M10-D2-widgets.md` S1), so the keys are emitted in the order the
     * rows use and an optional key is emitted only when it was present.
     */
    toMondaySettings(settings) {
        const out = {
            x_axis_columns: settings.xAxisColumns,
            y_axis_columns: settings.yAxisColumns,
            z_axis_columns: settings.zAxisColumns,
            guideline_base: settings.guidelineBase,
            graph_type: settings.graphType,
            empty_values: settings.emptyValues,
            group_by: settings.groupBy,
        };
        if (settings.flatLegendType !== null)
            out['flat_legend_type'] = settings.flatLegendType;
        if (settings.showCumulativeData)
            out['show_comulative_data'] = true;
        if (settings.benchmarkLine !== null) {
            out['benchmark_line_label'] = settings.benchmarkLine.label;
            out['benchmark_line_color'] = settings.benchmarkLine.color;
            out['benchmark_line_value'] = settings.benchmarkLine.value;
        }
        if (settings.createdBy !== null)
            out['createdBy'] = settings.createdBy;
        if (settings.calcFunctionType !== null)
            out['calc_function_type'] = settings.calcFunctionType;
        if (settings.boardIds.length > 0)
            out['board_ids'] = settings.boardIds;
        for (const [key, value] of Object.entries(settings.extra))
            out[key] = value;
        return out;
    },
    dataRequest(settings) {
        const [x] = activeAxisKeys(settings.xAxisColumns);
        const measures = activeAxisKeys(settings.yAxisColumns).map((key) => ({
            key,
            columnId: key === COUNT_MEASURE_KEY ? null : key,
            func: (key === COUNT_MEASURE_KEY ? 'count' : (settings.calcFunctionType ?? 'sum')),
        }));
        return {
            dimension: x === undefined
                ? { kind: 'none' }
                : x === GROUP_DIMENSION_KEY
                    ? { kind: 'group' }
                    : { kind: 'column', columnId: x },
            dateBucket: settings.groupBy,
            // An empty y axis is monday's own default chart: count of items.
            measures: measures.length > 0 ? measures : [{ key: COUNT_MEASURE_KEY, columnId: null, func: 'count' }],
            includeEmpty: settings.emptyValues,
            boardIds: settings.boardIds,
        };
    },
});
function safeParse(raw) {
    try {
        const parsed = JSON.parse(raw);
        return parsed !== null && typeof parsed === 'object' ? parsed : {};
    }
    catch {
        return {};
    }
}
/** The pixel height of the chart card monday actually ships, for the geometry tests. */
export const CHART_CARD_HEIGHT_PX = widgetPixelHeight(6);
//# sourceMappingURL=index.js.map